'use client';

import { useMemo } from 'react';
import Link from 'next/link';
import {
  Button,
  Container,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  Title,
  TypographyStylesProvider,
} from '@mantine/core';
import type { Locale } from '../../i18n/locales';
import type { SiteSettingsData, BlogPost } from '../../types/content';
import { Header } from '../layout/Header';
import { Footer } from '../layout/Footer';
import { PublicPageShell } from '../layout/PublicPageShell';
import { JsonLd } from '../JsonLd';
import { siteUrl } from '../../lib/site';
import { resolveHref } from '../../lib/content/localization';
import { useRealtimeRefresh } from '../../hooks/useRealtimeRefresh';
import { MantineNextImage } from '../ui/MantineNextImage';

const hasHtmlMarkup = (value: string) => /<[a-z][\s\S]*>/i.test(value);

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const readTagAttr = (tag: string, attr: string): string | undefined => {
  const escaped = attr.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = tag.match(new RegExp(`${escaped}=(["'])(.*?)\\1`, 'i'));
  return match?.[2];
};

const writeTagAttr = (tag: string, attr: string, value: string): string => {
  const escaped = attr.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const attrRegex = new RegExp(`${escaped}=(["']).*?\\1`, 'i');
  if (attrRegex.test(tag)) {
    return tag.replace(attrRegex, `${attr}="${value}"`);
  }
  const isSelfClosing = /\/>$/.test(tag);
  if (isSelfClosing) return tag.replace(/\/>$/, ` ${attr}="${value}" />`);
  return tag.replace(/>$/, ` ${attr}="${value}">`);
};

const isPlaceholderImageSrc = (src?: string): boolean => !!src && /^data:image\//i.test(src);

const normalizeAssetUrl = (value?: string): string | undefined => {
  if (!value) return undefined;
  try {
    const url = new URL(value, 'https://vakuutuslaskuri.fi');
    return `${url.origin}${url.pathname}`.replace(/\/+$/, '');
  } catch {
    return value.split('?')[0]?.replace(/\/+$/, '');
  }
};

const toImageIdentity = (value?: string): string | undefined => {
  const normalized = normalizeAssetUrl(value);
  if (!normalized) return undefined;
  try {
    const url = new URL(normalized);
    const decodedPath = decodeURIComponent(url.pathname);
    const identityPath = decodedPath
      .replace(/-\d+x\d+(?=\.[a-z0-9]+$)/i, '')
      .replace(/-scaled(?=\.[a-z0-9]+$)/i, '')
      .toLowerCase();
    return `${url.origin.toLowerCase()}${identityPath}`;
  } catch {
    return normalized
      .replace(/-\d+x\d+(?=\.[a-z0-9]+$)/i, '')
      .replace(/-scaled(?=\.[a-z0-9]+$)/i, '')
      .toLowerCase();
  }
};

const getFirstImageSrc = (html: string): string | undefined => {
  const imgMatch = html.match(/<img\b[^>]*>/i);
  if (!imgMatch) return undefined;
  const imgTag = imgMatch[0];
  return readTagAttr(imgTag, 'src') || readTagAttr(imgTag, 'data-src') || readTagAttr(imgTag, 'data-lazy-src');
};

const stripDuplicateLeadImage = (html: string, featuredImageUrl?: string): string => {
  const featuredIdentity = toImageIdentity(featuredImageUrl);
  if (!featuredIdentity) return html;

  let removed = false;

  const withoutMatchingFigure = html.replace(/<figure\b[\s\S]*?<\/figure>/gi, (figureHtml) => {
    if (removed) return figureHtml;
    const figureFirstSrc = getFirstImageSrc(figureHtml);
    if (toImageIdentity(figureFirstSrc) === featuredIdentity) {
      removed = true;
      return '';
    }
    return figureHtml;
  });

  if (removed) return withoutMatchingFigure.trimStart();

  return withoutMatchingFigure
    .replace(/<img\b[^>]*>/i, (imgTag) => {
      const imgSrc = getFirstImageSrc(imgTag);
      if (toImageIdentity(imgSrc) === featuredIdentity) {
        removed = true;
        return '';
      }
      return imgTag;
    })
    .trimStart();
};

const normalizeLazyLoadedImages = (html: string): string =>
  html.replace(/<img\b[^>]*>/gi, (tag) => {
    let normalized = tag;

    const dataSrc = readTagAttr(normalized, 'data-src') || readTagAttr(normalized, 'data-lazy-src');
    const src = readTagAttr(normalized, 'src');
    if (dataSrc && (isPlaceholderImageSrc(src) || !src)) {
      normalized = writeTagAttr(normalized, 'src', dataSrc);
    }

    const dataSrcSet = readTagAttr(normalized, 'data-srcset') || readTagAttr(normalized, 'data-lazy-srcset');
    const srcSet = readTagAttr(normalized, 'srcset');
    if (dataSrcSet && (!srcSet || /^data:image\//i.test(srcSet))) {
      normalized = writeTagAttr(normalized, 'srcset', dataSrcSet);
    }

    const dataSizes = readTagAttr(normalized, 'data-sizes');
    const sizes = readTagAttr(normalized, 'sizes');
    if (dataSizes && (!sizes || sizes === 'auto')) {
      normalized = writeTagAttr(normalized, 'sizes', dataSizes);
    }

    return normalized;
  });

const preserveEditorBlankParagraphs = (html: string): string =>
  html.replace(/<p(\s[^>]*)?>((?:[\s\u00a0]|&nbsp;?|<br\b[^>]*>)*)<\/p>/gi, (_match, attrs = '') => {
    return `<p${attrs} data-editor-spacer="true" aria-hidden="true"><br /></p>`;
  });

const toRichHtml = (value: string) => {
  if (!value) return '';
  if (hasHtmlMarkup(value)) return preserveEditorBlankParagraphs(normalizeLazyLoadedImages(value));

  const html = value
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)
    .map((paragraph) => `<p>${escapeHtml(paragraph)}</p>`)
    .join('\n');
  return preserveEditorBlankParagraphs(normalizeLazyLoadedImages(html));
};

export function BlogPostView({
  post,
  posts,
  locale,
  siteSettings,
  postPathPrefix,
  canonicalPath,
  showShareActions = true,
}: {
  post: BlogPost;
  posts: BlogPost[];
  locale: Locale;
  siteSettings: SiteSettingsData;
  postPathPrefix?: string;
  canonicalPath?: string;
  showShareActions?: boolean;
}) {
  const bodyHtml = stripDuplicateLeadImage(toRichHtml(post.body || post.excerpt || ''), post.imageUrl);
  const relatedPosts = posts.filter((item) => item.slug !== post.slug).slice(0, 3);
  const publishedDate = post.publishedAt
    ? new Date(post.publishedAt).toLocaleDateString(locale === 'fi' ? 'fi-FI' : locale === 'sv' ? 'sv-SE' : 'en-US')
    : null;
  const rawPrefix = postPathPrefix ?? '/blogi';
  const normalizedPrefix = rawPrefix.replace(/\/+$/, '');
  const resolvePostHref = (slug: string) => {
    const normalizedSlug = slug.replace(/^\/+/, '');
    const joined = `${normalizedPrefix}/${normalizedSlug}`.replace(/\/+/g, '/');
    return resolveHref(locale, joined.startsWith('/') ? joined : `/${joined}`);
  };
  const resolvedCanonicalPath = canonicalPath || `/blogi/${post.slug}`;
  const refreshSubscriptions = useMemo(
    () => [{ table: 'blog_posts', filter: `slug=eq.${post.slug}` }],
    [post.slug],
  );
  useRealtimeRefresh(refreshSubscriptions);

  return (
    <PublicPageShell>
      <Header locale={locale} settings={siteSettings} />

      <Container size="lg" py={{ base: 42, md: 64 }}>
        <Stack gap={24}>
          {post.imageUrl ? (
            <Paper p={8} radius={36} withBorder style={{ borderColor: 'var(--surface-border)', background: '#fff' }}>
              <MantineNextImage
                src={post.imageUrl}
                alt={post.imageAlt || post.title}
                radius={28}
                h={{ base: 230, md: 400 }}
                fit="cover"
                style={{ maxHeight: 400 }}
              />
            </Paper>
          ) : null}

          <Stack gap={10}>
            {publishedDate ? <Text size="xs" style={{ color: 'var(--text-muted)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>{publishedDate}</Text> : null}
            <Title
              order={1}
              style={{
                color: 'var(--text-primary)',
                fontWeight: 500,
                lineHeight: 1.2,
                letterSpacing: '-0.03em',
                fontSize: 'clamp(2rem, 4.2vw, 3.4rem)',
                maxWidth: 920,
              }}
            >
              {post.title}
            </Title>
            {post.excerpt ? (
              <Text style={{ color: 'var(--text-secondary)', lineHeight: 1.75, maxWidth: 900 }}>
                {post.excerpt}
              </Text>
            ) : null}
          </Stack>

          <Paper p={{ base: 20, md: 30 }} radius={34} withBorder style={{ borderColor: 'var(--surface-border)', background: '#fff' }}>
            {bodyHtml ? (
              <TypographyStylesProvider>
                <div className="content-rich-text blog-rich-text" dangerouslySetInnerHTML={{ __html: bodyHtml }} />
              </TypographyStylesProvider>
            ) : null}
          </Paper>

          {showShareActions ? (
            <Paper p={{ base: 16, md: 20 }} radius={30} withBorder style={{ borderColor: 'var(--surface-border)', background: 'var(--surface-soft)' }}>
              <Stack gap={10}>
                <Text fw={600} style={{ color: 'var(--text-primary)' }}>
                  {locale === 'fi' ? 'Jaa artikkeli' : locale === 'sv' ? 'Dela artikeln' : 'Share article'}
                </Text>
                <SimpleGrid cols={{ base: 2, sm: 4 }} spacing={8}>
                  {['Facebook', 'X', 'LinkedIn', 'WhatsApp'].map((channel) => (
                    <Button
                      key={channel}
                      variant="default"
                      styles={{
                        root: {
                          background: '#fff',
                          borderColor: 'var(--surface-border)',
                          color: 'var(--text-primary)',
                          borderRadius: 999,
                          height: 40,
                        },
                      }}
                    >
                      {channel}
                    </Button>
                  ))}
                </SimpleGrid>
              </Stack>
            </Paper>
          ) : null}
        </Stack>
      </Container>

      {relatedPosts.length ? (
        <Container size="lg" pb={80}>
          <Stack gap={20}>
            <Title order={2} style={{ color: 'var(--text-primary)', fontWeight: 500, fontSize: 'clamp(2rem, 3.8vw, 2.5rem)' }}>
              {locale === 'fi' ? 'Aiheeseen liittyvät artikkelit' : locale === 'sv' ? 'Relaterade artiklar' : 'Related articles'}
            </Title>
            <SimpleGrid cols={{ base: 1, md: 3 }} spacing={20}>
              {relatedPosts.map((item) => (
                <Paper
                  key={item.slug}
                  component={Link}
                  href={resolvePostHref(item.slug)}
                  p={0}
                  radius={40}
                  withBorder
                  style={{
                    overflow: 'hidden',
                    textDecoration: 'none',
                    color: 'inherit',
                    borderColor: 'var(--surface-border)',
                    background: '#fff',
                  }}
                >
                  {item.imageUrl ? <MantineNextImage src={item.imageUrl} alt={item.imageAlt || item.title} h={185} fit="cover" /> : null}
                  <Stack gap={8} p={20} style={{ minHeight: 220 }}>
                    <Text fw={600} size="lg" style={{ color: 'var(--text-primary)', lineHeight: 1.4 }}>
                      {item.title}
                    </Text>
                    <Text size="sm" style={{ color: 'var(--text-secondary)', lineHeight: 1.7 }} lineClamp={4}>
                      {item.excerpt}
                    </Text>
                    <Group mt="auto">
                      <Text size="sm" fw={600} style={{ color: 'var(--brand-primary)' }}>
                        {locale === 'fi' ? 'Lue artikkeli' : locale === 'sv' ? 'Läs artikel' : 'Read article'}
                      </Text>
                    </Group>
                  </Stack>
                </Paper>
              ))}
            </SimpleGrid>
          </Stack>
        </Container>
      ) : null}

      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: post.title,
          datePublished: post.publishedAt,
          image: post.imageUrl ? [post.imageUrl] : undefined,
          mainEntityOfPage: `${siteUrl}${resolvedCanonicalPath}`,
        }}
      />

      <Footer locale={locale} settings={siteSettings} />
    </PublicPageShell>
  );
}
